import { useEffect, useState } from 'react';
import { ApplicationPickerItem, DEFAULT_ITEMS } from '../components/AppMenu/applicationPickerItems';
import useClient from './useClient';
import {
  listUserAccessesByUserId,
  getConfig,
  WaffleMenuConfig,
  WaffleMenuLinkItem,
} from '#/tis/graphql';
import useAuthToken from '#/common/hooks/useAuthToken';
import uniq from 'lodash/uniq';

/**
 * Converts FontAwesome icon name from PascalCase (e.g., "faChartMixed") to kebab-case (e.g., "chart-mixed")
 */
const convertIconName = (faIconName: string): string => {
  // "faDisplayChartUp" → "display-chart-up"
  const kebab = faIconName
    .replace(/^fa/, '')
    .replaceAll(/([A-Z])/g, '-$1')
    .toLowerCase()
    .replace(/^-/, '');

  return kebab === 'unknown' ? 'grid' : kebab;
};

/**
 * Converts WaffleMenu link items to ApplicationPickerItems, filtering by user access
 */
const convertToPickerItems = (
  linkGroups: WaffleMenuLinkItem[][],
  userAppIds: string[],
): ApplicationPickerItem[] => {
  const result: ApplicationPickerItem[] = [];

  linkGroups.forEach((group, groupIndex) => {
    // Filter items based on user access
    const visibleItems = group.filter((item) => {
      // Items without AppId are always visible
      if (!item.AppId) {
        return true;
      }
      // Items with AppId are visible only if user has access
      return userAppIds.includes(item.AppId);
    });

    // Skip empty groups
    if (visibleItems.length === 0) {
      return;
    }

    // Add separator between groups (not before the first group)
    if (groupIndex > 0 && result.length > 0) {
      result.push('separator');
    }

    // Add visible items from this group
    visibleItems.forEach((item) => {
      result.push({
        id: item.AppId || item.Name,
        name: item.Name,
        icon: convertIconName(item.Icon),
        url: item.Navigate,
        authType: item.AuthType,
        postEndpoint: item.PostEndpoint,
      });
    });
  });

  return result;
};

const useApplicationPickerItems = (): ApplicationPickerItem[] => {
  const [items, setItems] = useState<ApplicationPickerItem[]>([]);

  const client = useClient();
  const token = useAuthToken();
  const userId = token?.uId;

  useEffect(() => {
    if (!client || !userId) {
      console.error('No Client or UserID!', { client, userId });
      setItems(DEFAULT_ITEMS);
      return;
    }

    // Fetch both user accesses and waffle menu config in parallel
    Promise.all([
      client.query({
        query: listUserAccessesByUserId,
        variables: { userId },
      }),
      client.query({
        query: getConfig,
        variables: { name: 'WaffleMenu', type: 'HubConfig' },
      }),
    ])
      .then(([userAccessResponse, configResponse]) => {
        // Extract user app IDs
        const appIds = uniq(
          userAccessResponse.data.listUserAccessesByUserId?.items?.map((item) => item.appId) ?? [],
        ).filter((id): id is string => !!id);

        // Parse waffle menu config
        const configValue = configResponse.data.getConfig?.value;
        if (!configValue) {
          console.error('WaffleMenu config not found');
          setItems(DEFAULT_ITEMS);
          return;
        }

        let waffleConfig: WaffleMenuConfig;
        try {
          waffleConfig = JSON.parse(configValue) as WaffleMenuConfig;
        } catch (error) {
          console.error('Failed to parse WaffleMenu config:', error);
          setItems(DEFAULT_ITEMS);
          return;
        }

        // Validate config structure
        if (!Array.isArray(waffleConfig.LinkGroups)) {
          console.error('WaffleMenu config has invalid structure: LinkGroups is not an array');
          setItems(DEFAULT_ITEMS);
          return;
        }

        // Convert to picker items with filtering
        const result = convertToPickerItems(waffleConfig.LinkGroups, appIds);
        setItems(result);
      })
      .catch((error) => {
        console.error('Failed to fetch application picker items.', error);
        setItems(DEFAULT_ITEMS);
      });
  }, [userId, client]);

  return items;
};

export default useApplicationPickerItems;
